import * as fflate from 'fflate';
import { saveAs } from 'file-saver';
import Pqueue from 'p-queue';

import transactionModel from '@/models/transaction';
import { isIOSSafari } from '@/utils/device';

function getMime(format = 'jpg') {
	return {
		jpg: 'image/jpeg',
		png: 'image/png'
	}?.[format];
}

// Safari hack to trigger download prompt without rendering the file
// in the browser, since Safari automatically opens any file that it can render
// (PDFs, images, etc.) instead of downloading them. This creates a hidden iframe
// and targets it with a link click to bypass Safari's built-in file viewer
function safariDownloadHack(url) {
	const iframeTitle = 'safari_download';
	let iframe = document.querySelector(`iframe[name="${iframeTitle}"]`);
	if (!iframe) {
		iframe = document.createElement('iframe');
		iframe.name = iframeTitle;
		iframe.title = iframeTitle;
		iframe.style.display = 'none';
		document.body.appendChild(iframe);
	}

	// Create link that targets the iframe
	const link = document.createElement('a');
	link.href = url;
	link.target = iframeTitle;

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

export async function pdfToZipStream(
	pdfUrl,
	{
		compressionLevel = 6,
		concurrency = 10,
		signal,
		format,
		filename = 'page'
	} = {}
) {
	const queue = new Pqueue({ concurrency });
	const zip = new fflate.Zip();

	const { getDocument } = await import('@denodecom/pdfjs-dist');
	const pdf = await getDocument({ url: pdfUrl }).promise;
	const pageCount = pdf.numPages;

	const ac = new AbortController();

	signal?.addEventListener('abort', () => ac.abort());

	const cleanup = () => {
		ac.abort();
		zip.terminate();
	};

	const stream = new ReadableStream({
		async start(controller) {
			let hasError = false;

			const handleError = err => {
				if (!hasError) {
					controller.error(err);
					cleanup();
					hasError = true;
				}
			};

			zip.ondata = (err, chunk, final) => {
				if (err) {
					return handleError(err);
				}

				controller.enqueue(chunk);

				if (final) {
					controller.close();
				}
			};

			for (let i = 1; i <= pageCount; i++) {
				queue
					.add(async () => {
						const page = await pdf.getPage(i);
						const viewport = page.getViewport({ scale: 2 });

						const canvas = document.createElement('canvas');

						canvas.width = viewport.width;
						canvas.height = viewport.height;

						const context = canvas.getContext('2d');

						await page.render({ canvasContext: context, viewport })
							.promise;

						const mime = getMime(format);

						const blob = await new Promise(resolve => {
							canvas.toBlob(resolve, mime, 1);
						});

						if (!blob) {
							throw new Error(`Error converting page ${i} to blob`);
						}

						const arrayBuffer = await blob.arrayBuffer();
						const buffer = new Uint8Array(arrayBuffer);

						const parsedName = `${filename}-${i}.${format}`;

						const zipEntry = !compressionLevel
							? new fflate.ZipPassThrough(parsedName)
							: new fflate.ZipDeflate(parsedName, {
									level: compressionLevel
								});

						const { ondata } = zipEntry;

						zipEntry.ondata = (err, data, final) => {
							ondata(err, data, final);

							if (final) {
								const internalEntry = zip.u[zip.u.length - 1];
								zipEntry.d = null;
								internalEntry.d = null;
							}
						};

						zip.add(zipEntry);

						zipEntry.push(buffer, true);
					})
					.catch(handleError);
			}

			await queue.onIdle();

			zip.end();
		},
		cancel(reason) {
			console.warn('Canceled:', reason);
			cleanup();
		}
	});

	return stream;
}

export async function downloadZipFromStream(
	stream,
	filename = 'pdfe-descarga.zip'
) {
	const reader = stream.getReader();
	const chunks = [];

	// eslint-disable-next-line no-constant-condition
	while (true) {
		const { done, value } = await reader.read();

		if (done) break;

		chunks.push(value);
	}

	const blob = new Blob(chunks, { type: 'application/zip' });

	saveAs(blob, `${filename}.zip`);
}

export async function downloadZip(id, { format, filename }) {
	const baseFormat = 'pdf';

	const url = transactionModel.downloadUrl(id, baseFormat);

	const stream = await pdfToZipStream(url, { format, filename });

	return downloadZipFromStream(stream, filename);
}

export async function genericDownload(id, { format, filename }) {
	const url = transactionModel.downloadUrl(id, format, filename);

	if (format === 'pdf' && isIOSSafari()) {
		return safariDownloadHack(url);
	}

	const response = await fetch(url);

	if (!response.ok) {
		throw new Error(response.statusText);
	}

	saveAs(await response.blob(), `${filename}.${format}`);
}

export async function checkIfFileNeedsPassword(id) {
	try {
		const { getDocument } = await import('@/pdfjs');

		const url = transactionModel.downloadUrl(id);

		await getDocument({ url }).promise;
	} catch (error) {
		if (error.name === 'PasswordException') {
			return true;
		}
		throw error;
	}

	return false;
}
